import { useState } from "react";
import { FiDownloadCloud, FiUploadCloud } from "react-icons/fi";
import { ModalDialog } from "common/Dialog";
import { Form, FormField, PrimaryButton, SecondaryButton } from "common/Forms";
import { FileInput } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";

// Only CSV and XLSX files are accepted.
const ACCEPTED_EXTENSIONS = [".csv", ".xlsx"];
const ACCEPT_ATTR =
  ".csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Sample template lives in /public, so it is served from the site root.
const SAMPLE_CSV_URL = "/sample-sales-leads.csv";
const SAMPLE_CSV_NAME = "sample-sales-leads.csv";

const downloadSampleCsv = () => {
  const link = document.createElement("a");
  link.href = SAMPLE_CSV_URL;
  link.download = SAMPLE_CSV_NAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const isAcceptedFile = (file) => {
  if (!file?.name) return false;
  const lowerName = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ImportSalesLead = ({ isOpened, onCancel, onImport, isLoading }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");

  const resetState = () => {
    setFile(null);
    setError("");
  };

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) {
      setFile(null);
      setError("");
      return;
    }
    if (!isAcceptedFile(selectedFile)) {
      setFile(null);
      setError("Only .csv and .xlsx files are allowed.");
      return;
    }
    setError("");
    setFile(selectedFile);
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const handleImport = async () => {
    if (!file || !isAcceptedFile(file)) {
      setError("Please select a .csv or .xlsx file.");
      return;
    }
    const succeeded = await onImport(file);
    if (succeeded) {
      resetState();
    }
  };

  return (
    <ModalDialog
      isOpened={isOpened}
      title="Import Sales Leads"
      onClose={handleCancel}
      classNames={["w-[460px] max-w-[92vw]"]}>
      <Form>
        <p className="mb-3 text-sm leading-relaxed text-gray-600">
          Upload a <span className="font-semibold text-black">.csv</span> or{" "}
          <span className="font-semibold text-black">.xlsx</span> file to bulk-import sales leads.
        </p>

        <p className="mb-4 flex items-center gap-2 text-sm text-gray-600">
          <FiDownloadCloud className="shrink-0 text-[15px] text-[#3379db]" />
          <button
            type="button"
            onClick={downloadSampleCsv}
            className="font-medium text-[#3379db] underline underline-offset-2 hover:text-[#3372c9]">
            Click here to download a sample file
          </button>
        </p>

        <FormField
          id="import_sales_lead_file"
          inputFactory={(id, name, onValidation) => (
            <FileInput
              id={id}
              name={name}
              accept={ACCEPT_ATTR}
              error={error}
              value={file}
              placeholder="Click to browse your file"
              helperText={file ? formatFileSize(file.size) : "Supported formats: .csv, .xlsx"}
              onChange={handleFileChange}
              onValidation={onValidation}
              skipInitialValidation={true}
            />
          )}
        />

        <Grid columns={2} columnGap={0.75} classNames={["mt-5"]}>
          <GridCell>
            <PrimaryButton isWide={true} disabled={!file || isLoading} action={handleImport}>
              {isLoading ? "Importing..." : "Import"}
            </PrimaryButton>
          </GridCell>
          <GridCell>
            <SecondaryButton isWide={true} disabled={isLoading} action={handleCancel}>
              Cancel
            </SecondaryButton>
          </GridCell>
        </Grid>
      </Form>
    </ModalDialog>
  );
};

export default ImportSalesLead;
